// Renderer for the "Dynamic At a glance (M&E)" layout.
//
// A separate module from atAGlanceParser, not a branch inside it, so the
// original dynamic-at-a-glance cannot regress.
//
// The data readers come from the hotel-profile screen: the block stores
// `apiData` as the verbatim /hotel-detail response, which is what those
// readers take, so the slide and the profile screen agree by construction.
import { buildAagExpandSection } from "./aagExpandSection";
import { HotelLocation } from "../../../../../editorjs/icons/icons";
import {
  capacityFor,
  decodeHTML,
  readArrival,
  readMeetingFeatures,
  readHotelGalleryCategories,
  readMeetingGallery,
  readMeetingRooms,
  readOutlets,
} from "../../../../rhgHotel/hotelData";

/* ── image sizing ─────────────────────────────────────────────────────────── */

// The CDN encodes the rendition in the filename, not a query string:
// …-f67041458_TH128.jpg is 128x128, _S 75x50, _M 390x260, _L 600x400,
// _XL 1023x682, _3XL 2879x1920. `urlThumbnail` points at TH128, so anything
// bigger than a favicon must ask for a larger variant by name.
const CDN_VARIANT = /_(?:TH\d+|XS|S|M|L|XL|\d?XL)(\.[a-z]+)$/i;

const sized = (url, variant) =>
  typeof url === "string" && CDN_VARIANT.test(url)
    ? url.replace(CDN_VARIANT, `_${variant}$1`)
    : url || "";

// The multimedia arrays mix stills and video renditions; a video URL in an
// <img> is just a broken tile.
const isStill = (url) =>
  typeof url === "string" && !!url && !url.includes("/video/");

/* ── gallery viewer ───────────────────────────────────────────────────────── */

const viewerOf = (el) =>
  el.closest(".at-a-glance-content-container")?.querySelector(".aag-me-viewer");

function viewerShow(viewer, index) {
  let slides = [];
  try {
    slides = JSON.parse(viewer.getAttribute("data-slides") || "[]");
  } catch (e) {
    slides = [];
  }
  if (!slides.length) return;

  const i = ((index % slides.length) + slides.length) % slides.length;
  viewer.setAttribute("data-index", String(i));

  const img = viewer.querySelector(".aag-me-viewer-img");
  img.src = slides[i].full;
  img.alt = slides[i].caption || "";
  viewer.querySelector(".aag-me-viewer-caption").textContent =
    slides[i].caption || "";
  const slide = slides[i];
  viewer.querySelector(".aag-me-viewer-count").textContent = slide.gt
    ? `${slide.gi} / ${slide.gt}`
    : `${i + 1} / ${slides.length}`;
  const groupEl = viewer.querySelector(".aag-me-viewer-group-name");
  if (groupEl) groupEl.textContent = slide.group || "";

  const active = viewer.querySelector(`.aag-me-viewer-thumb[data-index="${i}"]`);
  viewer.querySelectorAll(".aag-me-viewer-thumb").forEach((t) => {
    t.classList.toggle("is-active", t === active);
  });

  // Keep the active thumbnail in view, opening its group if it is collapsed.
  if (active) {
    const group = active.closest(".aag-me-viewer-group");
    if (group && !group.classList.contains("is-open")) {
      group.classList.add("is-open");
    }
    active.scrollIntoView({ block: "nearest" });
  }

  // The sidebar is lazy: only load the thumbnails of groups the viewer has
  // actually opened, otherwise a 229-image hotel fetches 229 files on open.
  viewer
    .querySelectorAll(".aag-me-viewer-group.is-open img[data-src]")
    .forEach((el) => {
      el.src = el.getAttribute("data-src");
      el.removeAttribute("data-src");
    });
}

window.aagMeViewerOpen = function (btn, index) {
  const viewer = viewerOf(btn);
  if (!viewer) return;
  viewer.classList.add("is-open");
  viewerShow(viewer, index);
};

window.aagMeViewerClose = function (btn) {
  const viewer = viewerOf(btn);
  if (!viewer) return;
  viewer.classList.remove("is-open");
  viewer.querySelector(".aag-me-viewer-img").src = "";
};

window.aagMeViewerGo = function (btn, index) {
  const viewer = viewerOf(btn);
  if (viewer) viewerShow(viewer, index);
};

window.aagMeViewerStep = function (btn, delta) {
  const viewer = viewerOf(btn);
  if (!viewer) return;
  viewerShow(viewer, Number(viewer.getAttribute("data-index") || 0) + delta);
};

window.aagMeViewerGroup = function (btn) {
  const group = btn.closest(".aag-me-viewer-group");
  group.classList.toggle("is-open");
  if (group.classList.contains("is-open")) {
    group.querySelectorAll("img[data-src]").forEach((el) => {
      el.src = el.getAttribute("data-src");
      el.removeAttribute("data-src");
    });
  }
};

/* ── panel switching ──────────────────────────────────────────────────────── */

// Every panel is rendered at once and the container attribute says which is
// visible, so switching is one attribute write.
window.aagMePanel = function (btn, panel) {
  const container = btn.closest(".at-a-glance-content-container");
  if (!container) return;

  const current = container.getAttribute("data-panel") || "hero";
  // Clicking the active button returns to the hero, so every button toggles.
  const next = current === panel ? "hero" : panel;
  container.setAttribute("data-panel", next);

  container.querySelectorAll(".aag-me-nav-btn").forEach((el) => {
    el.classList.toggle("is-active", el.getAttribute("data-panel") === next);
  });

  // Iframes stay unloaded until first use: 80 slides each booting a Google
  // map on render is what makes a deck crawl.
  if (next !== "hero") {
    container
      .querySelectorAll(`[data-panel-body="${next}"] iframe[data-src]`)
      .forEach((frame) => {
        frame.src = frame.getAttribute("data-src");
        frame.removeAttribute("data-src");
      });
  }

  // The key-features chevron only shows once the list overflows, and has to
  // wait for the panel transition or the element is still zero-height.
  if (next === "keyfeatures") {
    const scrollEl = container.querySelector(
      '[data-panel-body="keyfeatures"] .expand-scroll',
    );
    if (scrollEl && typeof window.aagUpdateExpandChevron === "function") {
      setTimeout(() => window.aagUpdateExpandChevron(scrollEl), 450);
    }
  }
};

// Widens the left column to the full slide, hiding the right column. The
// chevron is the only way back, so it stays put and flips to point left.
// Back to the cover resets the slide, not just the transition. aagInitialBack
// only moves between cover and detail, so whichever panel was open and any
// expanded column were still set when the cover was reopened - View Details
// then came back full-width on the last panel instead of the hero.
window.aagMeInitialBack = function (btn) {
  const container = btn.closest(".at-a-glance-content-container");
  if (!container) return;

  container.setAttribute("data-panel", "hero");
  container.querySelectorAll(".aag-me-nav-btn").forEach((el) => {
    el.classList.remove("is-active");
  });

  container.classList.remove("cols-expanded");
  const expand = container.querySelector(".aag-me-expand-cols");
  if (expand) {
    expand.setAttribute("aria-expanded", "false");
    expand.setAttribute("aria-label", "Expand to full width");
  }

  window.aagInitialBack(btn);
};

window.aagMeExpandColumns = function (btn) {
  const container = btn.closest(".at-a-glance-content-container");
  if (!container) return;
  const on = container.classList.toggle("cols-expanded");
  btn.setAttribute("aria-expanded", on ? "true" : "false");
  btn.setAttribute(
    "aria-label",
    on ? "Collapse to two columns" : "Expand to full width",
  );

  // The capacity table sizes its sticky columns against the panel width and
  // the map iframe re-centres on resize, so both need a nudge afterwards.
  setTimeout(() => window.dispatchEvent(new Event("resize")), 650);
};

// Toggles the directions card over the location map.
window.aagMeMapInfo = function (btn) {
  const wrap = btn.closest(".aag-me-map-wrap");
  if (!wrap) return;
  wrap.classList.toggle("show-info");
  btn.classList.toggle("is-on", wrap.classList.contains("show-info"));
};

/* ── helpers ──────────────────────────────────────────────────────────────── */

// Everything interpolated into the template is escaped. The payload is
// third-party content and some of it (outlet names, room titles) is free text.
const esc = (value) =>
  String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const text = (value) => esc(decodeHTML(String(value == null ? "" : value)));

const list = (value) => {
  if (value == null) return [];
  if (Array.isArray(value)) return value.filter((v) => v != null);
  if (typeof value === "object" && "values" in value) {
    const inner = value.values;
    if (Array.isArray(inner)) return inner.filter((v) => v != null);
    return inner == null ? [] : [inner];
  }
  return [value];
};

const tagHtml = (items) =>
  items.length
    ? `<div class="aag-me-tags">${items
        .map((t) => `<span class="aag-me-tag">${text(t)}</span>`)
        .join("")}</div>`
    : "";

// Nav-tab icons. Filled marks on a 1200 viewBox, drawn at 26px by the nav
// rule; `fill: currentColor` on the root so they take the button's red and
// any hover state, rather than the #c00c0c the source files bake in.
// Nav-tab icons. Filled marks on a 1200 viewBox, drawn at 26px by the nav
// rule; `fill: currentColor` on the root so they take the button's red and
// any hover state, rather than the #c00c0c the source files bake in.
// Path data is left at source precision: rounding it to 1dp truncated the
// key-features mark to a quarter of its canvas.
const ICONS = {
  map:
    '<svg viewBox="0 0 1200 1200" fill="currentColor"><path d="m1038 376.42c-6.5039-53.363-23.598-104.89-50.277-151.56-26.68-46.672-62.41-87.547-105.09-120.23-80.609-59.762-178.3-92.016-278.64-91.996-100.38-1.7891-198.68 28.707-280.42 86.992-43.273 31.859-79.742 72.043-107.27 118.19-27.531 46.148-45.566 97.332-53.047 150.55-17.52 110.88 4.5859 227.1 63.93 336.07 121.45 223 339.98 462.43 349.18 472.51 6.082 6.6367 14.672 10.418 23.676 10.418s17.598-3.7812 23.68-10.418c9.2305-10.07 227.48-249.25 348.92-472.11 56.629-99.562 79.559-214.77 65.367-328.42zm-121.73 297.62c-94.738 173.9-255.94 364.17-316.25 432.91-60.328-68.777-221.66-259.25-316.42-433.26-52.523-96.418-72.191-198.54-56.906-295.33 32.984-208.65 210.36-301.49 372.9-301.49h3.7891c163.43 1.4414 341.46 97.441 370.99 308.39 12.254 100.01-8.1289 201.3-58.105 288.79zm-316.27-437.52c-60.504 0-118.53 24.035-161.31 66.816s-66.816 100.8-66.816 161.31 24.035 118.53 66.816 161.31c42.781 42.785 100.8 66.816 161.31 66.816s118.53-24.031 161.31-66.816c42.781-42.781 66.816-100.8 66.816-161.31-0.066406-60.48-24.121-118.47-66.891-161.23-42.766-42.77-100.75-66.824-161.23-66.891zm0 392c-43.465-0.007813-85.145-17.281-115.88-48.02-30.727-30.734-47.988-72.422-47.98-115.89 0.003907-43.461 17.273-85.145 48.012-115.87 30.738-30.73 72.422-47.992 115.89-47.988 43.461 0.003907 85.145 17.273 115.88 48.012 30.73 30.734 47.992 72.418 47.988 115.88-0.050781 43.453-17.336 85.109-48.062 115.83-30.73 30.723-72.391 47.996-115.84 48.039z"/></svg>',
  capacity:
    '<svg viewBox="0 0 1200 1200" fill="currentColor"><path d="m600 262.5c-82.641 0-150.05 67.266-150.05 149.9 0 82.641 67.406 150.05 150.05 150.05s150.05-67.406 150.05-150.05-67.406-149.9-150.05-149.9zm0 37.359c62.344 0 112.55 50.203 112.55 112.55s-50.203 112.55-112.55 112.55-112.55-50.203-112.55-112.55 50.203-112.55 112.55-112.55zm-318.74 37.5c-72.281 0-131.34 59.109-131.34 131.39s59.109 131.26 131.34 131.26c72.281 0 131.21-58.969 131.21-131.26 0-72.285-58.969-131.39-131.21-131.39zm637.55 0c-72.281 0-131.21 59.109-131.21 131.39s58.969 131.26 131.21 131.26c72.281 0 131.16-58.969 131.16-131.26 0-72.285-58.922-131.39-131.16-131.39zm-637.55 37.547c51.984 0 93.703 41.812 93.703 93.844 0 51.984-41.719 93.703-93.703 93.703-51.984 0-93.844-41.719-93.844-93.703s41.859-93.844 93.844-93.844zm637.55 0c51.984 0 93.656 41.812 93.656 93.844 0 51.984-41.672 93.703-93.656 93.703-51.984 0-93.703-41.719-93.703-93.703s41.719-93.844 93.703-93.844zm-412.45 225.05c-40.359 0-76.594 18.375-100.73 47.203-15.281-6.1875-31.922-9.6094-49.359-9.6094h-149.9c-72.281 0-131.34 58.969-131.34 131.26v150.05c0.046875 10.359 8.4844 18.703 18.844 18.656h318.74v-0.09375h374.86v0.09375h318.74c10.359 0.046876 18.797-8.3438 18.844-18.656v-150.05c0-72.281-59.109-131.26-131.34-131.26h-149.95c-17.391 0-34.031 3.4219-49.312 9.6094-24.141-28.781-60.375-47.203-100.73-47.203zm0 37.5h187.4c51.984 0 93.844 41.719 93.844 93.703v168.74h-375.14v-168.74c0-51.984 41.859-93.703 93.844-93.703zm-300 37.641h149.9c10.219 0 20.109 1.5938 29.297 4.5938-6.7969 15.844-10.594 33.281-10.594 51.516v168.79h-262.45v-131.26c0-51.984 41.859-93.703 93.844-93.703zm637.4 0h149.9c51.984 0 93.844 41.672 93.844 93.703v131.26l-262.4-0.046876v-168.79c0-18.281-3.7969-35.719-10.594-51.516 9.1875-3 19.031-4.5938 29.25-4.5938z"/></svg>',
  features:
    '<svg viewBox="0 0 1200 1200" fill="currentColor"><path d="m912 156h-108v-6c0-29.766-24.234-54-54-54h-60c-3.375 0-6-2.625-6-6 0-29.766-24.234-54-54-54h-60c-29.766 0-54 24.234-54 54 0 3.375-2.625 6-6 6h-60c-29.766 0-54 24.234-54 54v6h-108c-39.703 0-72 32.297-72 72v864c0 39.703 32.297 72 72 72h624c39.703 0 72-32.297 72-72v-864c0-39.703-32.297-72-72-72zm-468-6c0-3.375 2.625-6 6-6h60c29.766 0 54-24.234 54-54 0-3.375 2.625-6 6-6h60c3.375 0 6 2.625 6 6 0 29.766 24.234 54 54 54h60c3.375 0 6 2.625 6 6v60c0 3.375-2.625 6-6 6h-300c-3.375 0-6-2.625-6-6zm492 942c0 13.219-10.781 24-24 24h-624c-13.219 0-24-10.781-24-24v-864c0-13.219 10.781-24 24-24h108v6c0 29.766 24.234 54 54 54h300c29.766 0 54-24.234 54-54v-6h108c13.219 0 24 10.781 24 24z"/><path d="m840 396h-300c-13.219 0-24 10.781-24 24s10.781 24 24 24h300c13.219 0 24-10.781 24-24s-10.781-24-24-24z"/><path d="m463.08 343.08-46.078 46.078-19.688-13.078c-11.062-7.3125-25.922-4.3125-33.234 6.6094s-4.3125 25.922 6.6094 33.234l36 24c4.0781 2.7656 8.625 4.0781 13.312 4.0781 6.2344 0 12.375-2.3906 16.922-7.0781l60-60c9.375-9.375 9.375-24.609 0-33.938-9.375-9.375-24.609-9.375-33.938 0z"/><path d="m840 576h-300c-13.219 0-24 10.781-24 24s10.781 24 24 24h300c13.219 0 24-10.781 24-24s-10.781-24-24-24z"/><path d="m463.08 523.08-46.078 46.078-19.688-13.078c-11.062-7.3125-25.922-4.3125-33.234 6.6094-7.3125 11.062-4.3125 25.922 6.6094 33.234l36 24c4.0781 2.7656 8.625 4.0781 13.312 4.0781 6.2344 0 12.375-2.3906 16.922-7.0781l60-60c9.375-9.375 9.375-24.609 0-33.938-9.375-9.375-24.609-9.375-33.938 0z"/><path d="m840 756h-300c-13.219 0-24 10.781-24 24s10.781 24 24 24h300c13.219 0 24-10.781 24-24s-10.781-24-24-24z"/><path d="m463.08 703.08-46.078 46.078-19.688-13.078c-11.062-7.3125-25.922-4.3125-33.234 6.6094-7.3125 11.062-4.3125 25.922 6.6094 33.234l36 24c4.0781 2.7656 8.625 4.0781 13.312 4.0781 6.2344 0 12.375-2.3906 16.922-7.0781l60-60c9.375-9.375 9.375-24.609 0-33.938-9.375-9.375-24.609-9.375-33.938 0z"/><path d="m840 936h-300c-13.219 0-24 10.781-24 24s10.781 24 24 24h300c13.219 0 24-10.781 24-24s-10.781-24-24-24z"/><path d="m463.08 883.08-46.078 46.078-19.688-13.078c-11.062-7.3125-25.922-4.3125-33.234 6.6094s-4.3125 25.922 6.6094 33.234l36 24c4.0781 2.7656 8.625 4.0781 13.312 4.0781 6.2344 0 12.375-2.3906 16.922-7.0781l60-60c9.375-9.375 9.375-24.609 0-33.938-9.375-9.375-24.609-9.375-33.938 0z"/></svg>',
  gallery:
    '<svg viewBox="0 0 1200 1200" fill="currentColor"><path d="m209.95 14.531h780.1c54.328 0 103.73 22.219 139.55 57.984 35.766 35.766 57.984 85.219 57.984 139.55v775.82c0 54.375-22.219 103.73-57.984 139.55-35.766 35.766-85.219 57.984-139.55 57.984h-780.1c-54.375 0-103.73-22.219-139.55-57.984-35.766-35.766-57.984-85.219-57.984-139.55v-775.82c0-54.375 22.219-103.73 57.984-139.55 35.766-35.766 85.172-57.984 139.55-57.984zm390.05 163.18c45.562 0 86.812 18.469 116.72 48.328 29.859 29.859 48.328 71.156 48.328 116.72s-18.469 86.812-48.328 116.72c-29.859 29.859-71.156 48.328-116.72 48.328s-86.812-18.469-116.72-48.328c-29.859-29.859-48.328-71.156-48.328-116.72s18.469-86.812 48.328-116.72c29.859-29.859 71.156-48.328 116.72-48.328zm77.766 87.281c-19.922-19.922-47.391-32.203-77.766-32.203s-57.844 12.328-77.766 32.203c-19.875 19.922-32.203 47.391-32.203 77.766s12.328 57.844 32.203 77.766c19.922 19.875 47.391 32.203 77.766 32.203s57.844-12.328 77.766-32.203c19.875-19.922 32.203-47.391 32.203-77.766s-12.328-57.844-32.203-77.766zm-176.72 710.53c-11.344 10.031-28.688 9-38.719-2.3906-10.031-11.344-8.9531-28.688 2.3906-38.719l90.469-79.828-204.24-227.81-283.45 219.61v141.47c0 39.188 16.078 74.766 41.859 100.59 25.828 25.828 61.453 41.859 100.59 41.859h780.1c39.141 0 74.766-16.031 100.59-41.859s41.859-61.453 41.859-100.59v-167.16l-234.79-195.14-396.71 349.97zm95.25-157.22 282.71-249.42c10.5-9.2812 26.156-9.0938 36.375 0l217.18 180.47v-537.32c0-39.188-16.078-74.766-41.859-100.59-25.828-25.828-61.453-41.859-100.59-41.859h-780.1c-39.141 0-74.766 16.031-100.59 41.859s-41.859 61.453-41.859 100.59v564.89l270-209.21c11.812-9.0938 28.641-7.125 38.016 4.3125l220.78 246.28z"/></svg>',
  dining:
    '<svg viewBox="0 0 1200 1200" fill="currentColor"><path d="m810.19 612.47v-369.44c-23.512 19.09-40.648 48.035-52.727 92.48-16.484 60.703-23.555 148.88-24.152 276.95 25.625 0 51.25 0.027344 76.875 0.015625zm-400.55 281.02c-47.426-30.797-86.91-72.824-114.72-122.23-28.488-50.641-44.758-109.09-44.758-171.26 0-48.148 9.8203-94.152 27.555-136.08 17.531-41.484 42.832-78.875 74.012-110.3 1.3047-42.422 2.4375-84.871 3.9258-127.01 0.3125-9.8945 8.6016-17.676 18.496-17.363 9.8945 0.3125 17.676 8.6016 17.363 18.496-2.9062 82.914-11.82 178-9.0273 260.01 0 17.574 6.3516 33.406 16.215 44.859 9.4102 10.914 21.871 17.844 34.695 18.285 10.773 0.17188 19.547 6.918 19.332 18.312-2.8633 128.89-5.7383 257.77-8.6016 386.66-0.015625 2.0117 0.25391 3.9102 0.80859 5.625 0.92188 2.1953 1.6992 3.8125 3.375 5.5859 1.3164 1.418 2.8203 2.4961 4.4219 3.2031 1.375 0.625 3.0898 0.93359 5.0156 0.93359h14.953c1.6992 0 3.4727-0.21094 5.0039-0.93359 1.6016-0.70703 3.1172-1.8008 4.4219-3.2031 1.375-1.4727 2.4531-3.1875 3.1602-5.0742 0.70703-1.8438 1.0469-3.9102 1.0352-6.0938l-8.5898-386.71c-0.12891-11.352 8.4766-18.113 19.262-18.312 12.871-0.45312 25.355-7.3711 34.766-18.297 9.8633-11.453 16.215-27.285 16.215-44.844 1.0078-86.586-6.0078-173.4-9.0273-260.01-0.3125-9.8945 7.4688-18.184 17.363-18.496 9.8945-0.3125 18.184 7.4688 18.496 17.363l0.93359 24.422 2.2383-0.15625c7.6406-0.48047 15.008-0.72266 22.023-0.72266 27.695 0 54.723 3.2617 80.676 9.3828 20.27 4.7891 39.898 11.367 58.691 19.547 18.965-41.457 45.129-67.266 80.008-83.992 2.6094-1.4727 5.6133-2.3086 8.8164-2.3086 9.9336 0 18 8.0664 18 18v140.73c31.707 31.395 57.445 68.84 75.305 110.38 18.227 42.363 28.348 89.109 28.348 138.11 0 47.809-9.6367 93.445-27.059 135.02-17.066 40.734-41.656 77.586-71.957 108.77l2.5938 119.05c0.12891 6.4492-1.1758 12.57-3.9414 18.172-2.6094 5.4141-6.5195 10.234-11.566 14.414-5.1875 3.9414-9.6367 6.7734-15.887 8.9141-6.1523 1.8984-11.609 2.8906-18.086 2.8906h-14.953c-6.3789 0-12.445-0.97656-18.086-2.9062-6.0664-2.2812-10.773-4.75-15.816-8.9023-5.0742-4.1523-8.957-8.957-11.609-14.344-2.75-6.2344-4.1094-11.395-3.9688-18.312l0.93359-42.336c-17.32 7.625-35.336 13.875-53.844 18.582-27.723 7.0742-56.766 10.828-86.598 10.828-17.406 0-34.609-1.3164-51.508-3.7969-8.9141-1.3164-17.816-3.0039-26.645-5.0469l0.32422 14.457c0.085937 6.875-0.99219 13.367-3.2734 19.348-2.2969 6.0508-5.8555 11.664-10.617 16.754-4.8047 5.1289-10.219 9.0586-16.102 11.652-6.6914 2.6211-12.219 4.0234-19.488 4.0234h-14.953c-6.8164 0-13.336-1.375-19.434-4.0234-5.9688-2.5938-11.367-6.5195-16.172-11.652-4.7617-5.0898-8.3203-10.73-10.617-16.754-2.2539-6.7734-3.3867-12.344-3.2617-19.559l1.375-61.754zm-59.781-483.11c-15.691 20.652-28.887 43.301-39.09 67.465-15.832 37.434-24.605 78.734-24.605 122.16 0 55.844 14.586 108.3 40.109 153.68 21.219 37.715 50.02 70.613 84.332 96.621l5.9102-265.77c-17.164-4.2812-32.84-14.398-44.945-28.445-15.266-17.73-25.086-41.855-25.086-68.328 1.3477-25.695 2.4219-51.508 3.375-77.387zm376.79-97.656c-17.262-7.6094-35.375-13.734-54.129-18.172-23.203-5.4844-47.496-8.3906-72.512-8.3906-7.0156 0-13.633 0.19922-19.785 0.58203l-2.9336 0.18359c3.0312 67.027 6.6172 135.51 6.6172 200.84 0.070312 26.461-9.75 50.586-25.016 68.316-12.117 14.074-27.793 24.195-44.945 28.473l7.0859 319.31c10.559 2.7227 21.488 4.9453 32.684 6.6055 14.996 2.2109 30.473 3.375 46.289 3.375 26.832 0 52.91-3.375 77.727-9.6953 22.324-5.6836 43.641-13.719 63.598-23.781l5.0898-231.9h-31.066c-9.8945-0.070313-17.93-8.1055-17.93-18-0.015624-141.38 7.2695-237.86 25.328-304.36 1.2461-4.5938 2.5508-9.043 3.8984-13.379zm123.02 477.52c16.074-21.062 29.551-44.223 39.926-68.996 15.605-37.246 24.25-78.223 24.25-121.24 0-44.023-9.0586-85.934-25.398-123.9-10.969-25.527-25.242-49.324-42.25-70.809v224.7zm-249.66-730.24c149.12 0 284.13 60.449 381.84 158.16s158.16 232.73 158.16 381.84-60.449 284.13-158.16 381.84-232.73 158.16-381.84 158.16-284.13-60.449-381.84-158.16-158.16-232.73-158.16-381.84 60.449-284.13 158.16-381.84 232.73-158.16 381.84-158.16zm356.39 183.62c-91.191-91.191-217.21-147.62-356.39-147.62-139.18 0-265.2 56.426-356.39 147.62-91.191 91.191-147.62 217.21-147.62 356.39 0 139.18 56.426 265.2 147.62 356.39 91.191 91.191 217.21 147.62 356.39 147.62 139.18 0 265.2-56.426 356.39-147.62 91.191-91.191 147.62-217.21 147.62-356.39 0-139.18-56.426-265.2-147.62-356.39zm-481.55-16.426c0-9.9336 8.0664-18 18-18 9.9336 0 18 8.0664 18 18v220.2c0 9.9336-8.0664 18-18 18-9.9336 0-18-8.0664-18-18zm-55.219 0c0-9.9336 8.0664-18 18-18 9.9336 0 18 8.0664 18 18v220.2c0 9.9336-8.0664 18-18 18-9.9336 0-18-8.0664-18-18zm391.03 421.27h-28.359c-2.3086 105.01-4.6211 210.02-6.918 315.03l0.3125 1.6875c0.36719 0.73828 1.1328 1.6172 2.2695 2.5508 1.375 1.1914 2.918 2 4.6328 2.5508 1.8281 0.625 3.9961 0.94922 6.4062 0.94922h14.953c2.1133 0 4.4062-0.21094 6.4062-0.93359 1.7148-0.58203 3.2617-1.3906 4.6328-2.5664 1.0781-0.89453 1.8281-1.7422 2.1836-2.4648 0.29688-0.53906 0.39844-1.1211 0.39844-1.7148-2.3086-105.02-4.6055-210.05-6.918-315.09z"/></svg>',
  keyfeatures:
    '<svg viewBox="0 0 1200 1200" fill="currentColor"><path d="m615.86 222.69 49.211 39.551-16.562 60.926c-2.6016 9.5859 0.89844 19.551 8.9375 25.387 4.1875 3.0391 9.0508 4.5625 13.914 4.5625 4.5 0 9-1.3008 12.988-3.9141l52.824-34.574 52.824 34.574c8.3125 5.4375 18.875 5.1875 26.898-0.64844 8.0391-5.8359 11.539-15.801 8.9375-25.387l-16.562-60.926 49.211-39.551c7.7383-6.2266 10.762-16.336 7.6875-25.789-3.0625-9.4375-11.461-15.863-21.375-16.336l-63.062-3.0742-22.414-59.023c-3.5234-9.2734-12.211-15.262-22.148-15.262s-18.625 5.9883-22.148 15.262l-22.414 59.023-63.062 3.0742c-9.9141 0.48828-18.312 6.8984-21.375 16.336-3.0703 9.4375-0.046875 19.562 7.6914 25.789zm87.098-8.8867c9.4141-0.46094 17.664-6.4492 21-15.25l13.211-34.801 13.211 34.812c3.3516 8.8125 11.586 14.801 21 15.25l37.188 1.8125-29.012 23.324c-7.3516 5.8984-10.5 15.586-8.0234 24.688l9.7617 35.926-31.148-20.387c-3.9375-2.5859-8.4609-3.875-12.988-3.875-4.5117 0-9.0391 1.2891-12.977 3.875l-31.148 20.387 9.7617-35.926c2.4766-9.0859-0.67578-18.773-8.0234-24.688l-29.023-23.324zm224.12 113.72c-0.73828 0-1.4609 0.039062-2.1992 0.039062 13.336-27.801 20.301-58.238 20.301-89.688 0-114.7-93.312-208.01-208.01-208.01s-208.01 93.324-208.01 208.02c0 37.637 10.199 74.289 29.164 106.21-106.68 8.7891-190.79 98.398-190.79 207.3 0 114.7 93.312 208.01 208.01 208.01 77.301 0 144.88-42.387 180.74-105.12 38.461 55.176 102.15 89.273 170.8 89.273 114.7 0 208.01-93.312 208.01-208.01 0-114.7-93.328-208.02-208.02-208.02zm-189.91-260.88c94.414 0 171.23 76.812 171.23 171.23 0 34.227-9.9609 66.988-28.875 95.176-56.102 13.199-104.6 49.324-133.25 99.637-32.5-46.613-83.574-79.352-142.41-87.387-24.488-30.363-37.926-68.238-37.926-107.43 0.011719-94.414 76.824-171.23 171.24-171.23zm-161.62 655.96c-94.414 0-171.23-76.812-171.23-171.23 0-94.414 76.812-171.23 171.23-171.23 94.414 0 171.23 76.812 171.23 171.23-0.003906 94.414-76.816 171.23-171.23 171.23zm351.54-15.852c-64.539 0-123.75-36.637-152.8-93.898 6.0234-19.438 9.2734-40.086 9.2734-61.488 0-27.988-5.5742-54.699-15.637-79.102 25.773-64.875 88.875-107.99 159.16-107.99 94.414 0 171.23 76.812 171.23 171.23 0 94.414-76.812 171.25-171.23 171.25zm129-212.21c-3.0742-9.4492-11.461-15.863-21.375-16.336l-63.062-3.0742-22.414-59.023c-3.5234-9.2734-12.211-15.262-22.148-15.262s-18.625 5.9883-22.148 15.262l-22.414 59.023-63.062 3.0742c-9.9141 0.48828-18.312 6.8984-21.375 16.336-3.0742 9.4492-0.050782 19.562 7.6875 25.789l49.211 39.551-16.562 60.926c-2.6016 9.5859 0.89844 19.551 8.9375 25.387 4.1875 3.0391 9.0508 4.5625 13.914 4.5625 4.5 0 9-1.3008 12.988-3.9141l52.824-34.574 52.824 34.574c8.3125 5.4375 18.875 5.1875 26.898-0.64844 8.0391-5.8359 11.539-15.801 8.9375-25.387l-16.562-60.926 49.211-39.551c7.7422-6.2148 10.754-16.34 7.6914-25.789zm-86.625 42.035c-7.3516 5.8984-10.5 15.602-8.0234 24.688l9.7617 35.926-31.164-20.398c-7.875-5.1484-18.062-5.1484-25.938 0.011719l-31.148 20.387 9.7617-35.926c2.4766-9.1016-0.67578-18.789-8.0117-24.688l-29.023-23.324 37.188-1.8125c9.4141-0.46094 17.648-6.4492 20.988-15.25l13.211-34.812 13.211 34.801c3.3359 8.8125 11.586 14.801 21 15.262l37.188 1.8125zm-88.512 244.25-176.69 102.91c-6.6758 3.8867-13.426 6.8867-20.387 9.0234 2.125-4.3008 3.8633-8.9375 5.1992-13.898 9.3984-35.102-11.414-71.738-47.398-83.414-0.78906-0.25-1.5859-0.44922-2.4141-0.60156-50.551-9.125-100.55-27.188-140.73-41.727-14.012-5.0625-27.25-9.8516-38.523-13.512-46.238-15.012-87.801 3.4258-122.36 23.426l-129.07 74.738-11.051-19.512c-5.8242-10.301-15.336-17.711-26.789-20.887-11.449-3.1758-23.426-1.7109-33.727 4.1133l-49.574 28.062c-21.312 12.062-28.836 39.211-16.773 60.512l145.75 257.52c8.1641 14.414 23.227 22.523 38.711 22.523 7.3984 0 14.914-1.8516 21.801-5.75l49.574-28.062c21.312-12.062 28.836-39.211 16.773-60.512l-13.699-24.211 26.664-15.387 225.21 60.352c12.727 3.4141 25.387 5.1016 37.836 5.1016 25.188 0 49.523-6.9375 71.773-20.637l320.65-197.44c30.426-18.738 40.125-65.852 20.363-98.863-9.5781-15.977-48.754-63.488-151.12-3.8789zm-572.57 323.48-49.574 28.062c-3.5859 2.0391-8.3516 0.71094-10.387-2.875l-145.74-257.54c-2.0391-3.6016-0.71094-8.3516 2.875-10.387l49.574-28.062c1.3125-0.73828 2.6016-0.96094 3.6758-0.96094 0.86328 0 1.6016 0.13672 2.1133 0.28906 1.1484 0.3125 3.2734 1.1992 4.6133 3.5625l145.75 257.52c2.0117 3.5859 0.69922 8.3477-2.9023 10.387zm684.04-252.05-320.64 197.44c-24.762 15.25-51.961 19.051-80.812 11.324l-232.57-62.312c-4.7109-1.2617-9.7266-0.60156-13.949 1.8359l-32.988 19.039-84.762-149.77 129.39-74.914c28.926-16.738 60.148-30.801 92.574-20.273 10.699 3.4609 23.062 7.9375 37.375 13.113 41.125 14.863 92.227 33.336 145.35 43.074 16.988 6.0391 26.477 22.414 22.148 38.551-5.6875 21.238-23.562 23.875-37.824 22.262-25.852-5.3242-50.477-12.352-76.523-19.789-12.426-3.5508-25.273-7.2266-38.5-10.762-9.8242-2.625-19.898 3.1992-22.523 13-2.625 9.8125 3.1992 19.898 13 22.523 12.926 3.4609 25.637 7.0859 37.914 10.602 27.012 7.7109 52.523 15 80.051 20.625 0.11328 0.023437 22.711 3.7109 22.711 3.7109 33.512 5.3633 63.25 0.125 90.926-15.988l176.69-102.91c49.414-28.773 87.188-32.137 101.04-9.0117 9.4805 15.793 5.4062 40.34-8.0664 48.641zm-824.24 28.637c1.4141 9.6758-5.2734 18.664-14.949 20.074-9.6758 1.4141-18.664-5.2734-20.074-14.949-1.4141-9.6758 5.2734-18.664 14.949-20.074 9.6719-1.4258 18.66 5.2773 20.074 14.949zm536.38-370.5c-3.0742-9.4492-11.461-15.863-21.375-16.336l-63.062-3.0742-22.414-59.023c-3.5234-9.2734-12.211-15.262-22.148-15.262s-18.625 5.9883-22.148 15.262l-22.414 59.023-63.051 3.0742c-9.9141 0.47656-18.312 6.8867-21.387 16.336-3.0742 9.4492-0.050781 19.574 7.6875 25.801l49.211 39.551-16.562 60.926c-2.6016 9.5859 0.89844 19.551 8.9375 25.387 4.1875 3.0391 9.0508 4.5625 13.914 4.5625 4.5 0 9-1.3008 12.988-3.9141l52.824-34.574 52.824 34.574c8.3125 5.4375 18.875 5.1875 26.914-0.66406 8.0391-5.8359 11.539-15.801 8.9375-25.375l-16.562-60.926 49.211-39.551c7.7266-6.2188 10.75-16.344 7.6758-25.797zm-86.625 42.039c-7.3516 5.8984-10.5 15.602-8.0234 24.688l9.7617 35.926-31.148-20.387c-7.8867-5.1641-18.074-5.1641-25.961 0l-31.148 20.387 9.7617-35.926c2.4766-9.1016-0.67578-18.789-8.0117-24.688l-29.023-23.324 37.188-1.8125c9.4141-0.46094 17.648-6.4492 20.988-15.25l13.211-34.812 13.211 34.812c3.3516 8.8008 11.586 14.801 21 15.25l37.188 1.8125z"/></svg>',
  website:
    '<svg viewBox="0 0 1200 1200" fill="currentColor"><path d="m726.94 963.28-0.79688-2.7656c-24.328 11.203-49.875 20.203-76.359 26.719-32.906 8.0625-67.125 12.375-102.23 12.375-117.89 0-224.63-47.812-301.87-125.06-77.242-77.25-125.06-183.98-125.06-301.87 0-117.89 47.812-224.63 125.06-301.87 77.25-77.242 183.98-125.06 301.87-125.06 117.89 0 224.63 47.812 301.92 125.06 77.25 77.25 125.06 183.98 125.06 301.87 0 24.141-2.0625 47.953-5.9531 71.297-2.9531 17.531-6.9844 34.734-12.047 51.516l21.984 5.4375c6.9844 1.7344 13.031 5.3906 17.625 10.359 4.5938 5.0156 7.6875 11.297 8.8594 18.469 1.125 7.125 0.14062 14.062-2.625 20.203-2.8125 6.1875-7.4062 11.531-13.5 15.375l-54.516 34.219 134.29 129.42c7.0312 6.75 10.641 15.797 10.828 24.891 0.1875 9.0469-3.0938 18.141-9.8438 25.172l-62.109 64.453c-6.75 7.0312-15.797 10.641-24.891 10.828-9.0469 0.14062-18.141-3.0938-25.172-9.8906l-133.92-129-31.734 55.641c-3.5625 6.2812-8.7188 11.109-14.812 14.156-6.1406 3.0938-13.078 4.3594-20.156 3.5156-7.125-0.84375-13.594-3.7031-18.797-8.1562-5.25-4.4531-9.1406-10.406-11.109-17.297zm-10.828-37.734-8.9531-31.172c-12.469 19.312-26.062 36.375-40.688 50.859 17.062-5.4375 33.656-12.047 49.641-19.688zm-287.53 19.641c-20.344-20.156-38.766-45.422-54.844-74.766-12.281-22.453-23.203-47.391-32.438-74.391-33.844 8.5312-64.312 19.078-90.422 31.219 6.4688 7.5469 13.219 14.812 20.25 21.844 43.5 43.594 97.312 76.922 157.45 96.094zm-200.34-146.86c29.297-14.391 63.891-26.766 102.42-36.609-14.344-51.891-23.016-109.83-24.422-171.1l-149.26 0.046875c3.5156 77.156 29.344 148.5 71.25 207.66zm-71.25-243.66h149.26c1.4062-61.219 10.078-119.16 24.422-171.1-38.531-9.8438-73.125-22.219-102.42-36.609-41.906 59.203-67.734 130.55-71.25 207.71zm93.891-236.63c26.109 12.141 56.578 22.688 90.422 31.219 9.2812-26.953 20.156-51.891 32.438-74.344 16.031-29.344 34.5-54.562 54.797-74.766-60.094 19.172-113.91 52.5-157.5 96.047-6.9844 7.0312-13.734 14.297-20.156 21.844zm415.64-117.89c20.344 20.156 38.766 45.422 54.844 74.766 12.281 22.453 23.156 47.391 32.438 74.344 33.844-8.5312 64.312-19.078 90.422-31.219-6.4688-7.5469-13.219-14.812-20.203-21.844-43.594-43.547-97.406-76.875-157.5-96.047zm200.29 146.86c-29.297 14.391-63.891 26.766-102.42 36.609 14.344 51.891 23.016 109.83 24.422 171.1h149.26c-3.5156-77.203-29.344-148.55-71.25-207.71zm71.25 243.66h-149.26c-0.23438 9.8438-0.65625 19.781-1.3125 29.766-0.65625 10.781-1.5938 21.609-2.7188 32.391l53.297 13.219 83.531 20.766c4.8281-15.797 8.6719-32.109 11.484-48.75 2.5781-15.422 4.2656-31.266 4.9688-47.391zm-372.56-407.44v189.66c54.75-0.98438 106.45-6.5625 153-15.75-8.2969-23.578-17.953-45.375-28.734-65.109-33.094-60.562-76.594-100.64-124.26-108.8zm0 225.71v145.74h187.31c-1.4062-58.641-9.7031-113.91-23.438-163.08-50.062 10.172-105.52 16.312-163.87 17.344zm0 181.74v145.74c18.562 0.32812 36.891 1.1719 54.984 2.5312 14.672 1.0781 29.062 2.4844 43.078 4.1719l-20.344-70.734c-0.1875-0.65625-0.32812-1.3594-0.46875-2.0156-1.2656-5.5312-1.2188-11.016 0.09375-16.219 1.4531-5.9531 4.5-11.438 8.9531-16.078 4.5-4.6406 9.8438-7.875 15.75-9.5625 5.8125-1.6875 12.047-1.7812 18.281-0.23438l63.797 15.844c0.84375-8.3906 1.5-17.016 2.0625-25.781 0.5625-8.9531 0.9375-18.188 1.1719-27.656zm0 181.74v189.71c21.328-3.6562 41.812-13.688 60.891-29.062 25.172-20.297 47.906-49.875 66.938-86.484l-6.7031-23.344-12.188-42.375c-18.375-2.5781-37.219-4.5938-56.438-6-17.016-1.3125-34.547-2.1094-52.5-2.4375zm-36 189.66v-189.66c-54.75 0.98438-106.45 6.5625-153 15.75 8.2969 23.578 17.953 45.375 28.734 65.109 33.141 60.609 76.641 100.69 124.26 108.8zm0-225.66v-145.74h-187.26c1.4062 58.641 9.7031 113.91 23.438 163.08 50.062-10.172 105.52-16.312 163.82-17.344zm0-181.74v-145.74c-58.359-1.0312-113.77-7.1719-163.82-17.344-13.734 49.172-22.031 104.44-23.438 163.08zm0-181.74v-189.71c-47.672 8.1562-91.172 48.188-124.26 108.8-10.781 19.688-20.391 41.531-28.734 65.109 46.594 9.2344 98.297 14.766 153 15.797zm191.72 440.39 39.984 138.89 31.453-55.125c2.7656-4.875 6.4219-8.8125 10.781-11.812v0.046875l0.046876-0.046875c4.4531-3 9.4688-4.9688 14.906-5.7656s10.781-0.32812 15.891 1.3125c5.1094 1.6406 9.7031 4.4062 13.688 8.25l133.97 129.14 61.406-63.75-134.39-129.52c-3.9844-3.8438-6.9375-8.3906-8.7656-13.406-1.8281-4.9688-2.4375-10.266-1.875-15.75 0.5625-5.5312 2.2969-10.641 5.1094-15.141 2.7656-4.4531 6.5625-8.25 11.297-11.25l54.047-33.938-139.22-34.594-151.45-37.594 27.609 95.953 0.28125 0.89063z" fill-rule="evenodd"/></svg>',
};

// The seating diagrams already ship with the theme, on the data-table
// layout, so they stay in step with the brand rather than being inlined.
const normaliseIconName = (value) =>
  String(value == null ? "" : value)
    .toLowerCase()
    .replace(/[^a-z]/g, "");

// Capacity columns are named by the feed; these are the ones whose label does
// not match an icon name directly.
const LAYOUT_ICON_ALIASES = {
  // Round-table seating. The theme's "banquet" is full rounds, chairs all the
  // way around.
  rounds: "banquet",
  round: "banquet",
  lunch: "banquet",
  // Dinner takes the one dinner-specific diagram rather than doubling up on
  // banquet: the feed publishes "Rounds" and a dinner column on the same
  // hotel, and with icon-only headings two identical diagrams are unreadable.
  dinner: "dinner dance",
  dinnerdance: "dinner dance",
  // Other feed wordings for diagrams that exist under a different name.
  reception: "drinks reception",
  cocktail: "drinks reception",
  standing: "drinks reception",
  hollowsquare: "hollow square",
  conference: "boardroom",
  schoolroom: "classroom",
  theater: "theatre",
};

const seatingIcon = (themeJson, label) => {
  const icons =
    (themeJson?.layouts || []).find((l) => l?.id === "data-table")?.icons || [];
  if (!icons.length) return "";
  const byName = (name) => icons.find((i) => normaliseIconName(i?.name) === name);
  // Each diagram ships twice: the plain one is #FFFFFF for the dark
  // data-table layout, the "… dark" one #333333. This header is light.
  const pick = (name) => byName(name + "dark") || byName(name);

  const key = normaliseIconName(label);
  if (!key) return "";

  // 1. The label naming a diagram outright, via the alias table or directly.
  let hit = pick(normaliseIconName(LAYOUT_ICON_ALIASES[key] || label));

  // 2. Otherwise the longest alias or diagram name the label CONTAINS - the
  //    feed qualifies its columns freely ("Dinner Set Up"), so an exact
  //    table cannot keep up. Longest first, or "dinnerdance" is swallowed
  //    by "dinner" and "hollowsquare" by "square".
  if (!hit) {
    const candidates = [
      ...Object.entries(LAYOUT_ICON_ALIASES),
      // The diagrams' own names, with the dark suffix off so both variants
      // collapse to the one candidate `pick` then re-resolves.
      ...icons.map((i) => {
        const n = normaliseIconName(i?.name).replace(/dark$/, "");
        return [n, n];
      }),
    ]
      .filter(([k]) => k && key.includes(k))
      .sort((a, b) => b[0].length - a[0].length);
    if (candidates.length) hit = pick(normaliseIconName(candidates[0][1]));
  }

  return hit?.icon || "";
};

const navBtn = (panel, label, icon) =>
  `<button type="button" class="aag-me-nav-btn" data-panel="${esc(panel)}"
     onclick="event.stopPropagation(); aagMePanel(this, '${esc(panel)}')">
     <span class="aag-me-nav-icon">${icon}</span>
     <span class="aag-me-nav-label">${label}</span>
   </button>`;

const panel = (name, body) =>
  `<div class="aag-me-panel" data-panel-body="${esc(name)}">${body}</div>`;

const emptyPanel = (message) =>
  `<div class="aag-me-empty">${text(message)}</div>`;

/* ── panels ───────────────────────────────────────────────────────────────── */

// Mirrors the hotel-profile capacity matrix: one column per layout the hotel
// actually publishes, "-" where a room does not offer it.
function capacityPanel(meeting, themeJson) {
  if (!meeting.rooms.length) {
    return emptyPanel("No meeting rooms published for this hotel.");
  }

  const cols = meeting.layouts;
  // Falls back to m² exactly as the hotel-profile capacity table does - the
  // two must not disagree on the same hotel.
  const sizeUnit = meeting.metric || "m\u00B2";
  // Each layout column gets the theme's seating diagram above its label; a
  // layout with no matching icon just shows the label.
  const head =
    `<tr><th class="aag-me-th-name">Meeting room</th>` +
    // The th is vertical-align: bottom, so the unit lands on the same baseline
    // as the other labels. An explicit break because the cell is nowrap.
    `<th>Size<br />${text(sizeUnit)}</th>` +
    cols
      .map((c) => {
        const icon = seatingIcon(themeJson, c.label);
        // Icon only, as on the data-table layout. The label stays in the title and
        // as screen-reader text so the column is still identifiable.
        return icon
          ? `<th title="${esc(c.label)}"><span class="aag-me-seat-icon">${icon}</span>` +
              `<span class="aag-me-seat-label is-sr">${text(c.label)}</span></th>`
          : `<th><span class="aag-me-seat-label">${text(c.label)}</span></th>`;
      })
      .join("") +
    `</tr>`;

  const rows = meeting.rooms
    .map((room) => {
      // Some rooms publish capacities with the layout taxonomy stripped. Showing
      // them bare under a labelled column would be a guess, so they get their
      // own full-width note, as on the profile screen.
      if (room.shapesUnlabelled) {
        // The note leads and the figures follow as prose: bare numbers first sat
        // under Theatre/Classroom/Boardroom and read as belonging to those columns.
        return `<tr class="aag-me-row-unlabelled">
          <td class="aag-me-td-name">${text(room.name)}</td>
          <td>${text(room.size)}</td>
          <td colspan="${cols.length || 1}">
            <span class="aag-me-note">Capacities published without layout
              types:</span>
            <span class="aag-me-bare">${room.shapes
              .map((s) => text(s.value))
              .join(", ")}</span>
          </td>
        </tr>`;
      }
      const cells = cols
        .map((c) => {
          const v = capacityFor(room, c.key);
          return `<td>${v == null ? "&ndash;" : text(v.toLocaleString())}</td>`;
        })
        .join("");
      return `<tr>
        <td class="aag-me-td-name">${text(room.name)}</td>
        <td>${text(room.size)}</td>${cells}
      </tr>`;
    })
    .join("");

  return `<div class="aag-me-panel-head"><h3>Capacity chart</h3></div>
    <div class="aag-me-table-scroll">
      <table class="aag-me-table"><thead>${head}</thead><tbody>${rows}</tbody></table>
    </div>`;
}

function featuresPanel(features, meeting) {
  if (
    !features.features.length &&
    !features.hotelDescription &&
    !meeting.rooms.length
  ) {
    return emptyPanel("No meeting and event features published.");
  }

  const counts = features.counts
    .map(
      (c) => `<div class="aag-me-count">
        <span class="aag-me-count-value">${text(c.text)}</span>
        <span class="aag-me-count-label">${text(c.label)}</span>
      </div>`,
    )
    .join("");

  // Proper cards rather than text blocks: each room leads with its own photo
  // and the figures an organiser scans for, then the description.
  const rooms = meeting.rooms
    .filter((r) => r.description || r.thumbnail)
    .map((r) => {
      const peak = r.shapes
        .map((sh) => Number(String(sh.value).replace(/[^\d]/g, "")))
        .filter((n) => Number.isFinite(n) && n > 0);
      const facts = [
        r.size ? `${text(r.size)} ${text(meeting.metric || "m\u00B2")}` : "",
        peak.length ? `up to ${Math.max(...peak)}` : "",
      ].filter(Boolean);

      return `<article class="aag-me-room-card">
        ${
          r.thumbnail
            ? `<div class="aag-me-room-card__media"><img data-src="${esc(
                sized(r.thumbnail, "L"),
              )}" alt="${esc(r.altText || r.name)}" loading="lazy" /></div>`
            : ""
        }
        <div class="aag-me-room-card__body">
          <h4>${text(r.name)}</h4>
          ${
            facts.length
              ? `<p class="aag-me-room-card__facts">${facts.join(" &middot; ")}</p>`
              : ""
          }
          ${r.description ? `<p>${text(r.description)}</p>` : ""}
        </div>
      </article>`;
    })
    .join("");

  // Heading, lede, chips, capacity and RFP line are one centred intro block,
  // wrapped so the centring stops at the room grid.
  // features.heading is deliberately not rendered: it labelled the chips,
  // which read as a set without it, and it broke the centred column.
  return `<div class="aag-me-features-intro">
      <div class="aag-me-panel-head"><h3>Meeting &amp; event features</h3></div>
      ${
        features.hotelDescription
          ? `<p class="aag-me-lede">${text(features.hotelDescription)}</p>`
          : ""
      }
      ${tagHtml(features.features.map((f) => f.label))}
      ${counts ? `<div class="aag-me-counts">${counts}</div>` : ""}
      ${
        features.rfpEmail
          ? `<p class="aag-me-rfp">RFP enquiries:
               <a href="mailto:${esc(features.rfpEmail)}">${text(
                 features.rfpEmail,
               )}</a></p>`
          : ""
      }
    </div>
    ${rooms ? `<div class="aag-me-room-grid">${rooms}</div>` : ""}`;
}

// M&E set first, then every other published category. Images already shown
// in the M&E group are dropped from later ones so the counter matches the
// number of distinct photos.
function galleryGroups(api, gallery) {
  const groups = [];
  const seen = new Set();

  const take = (images) =>
    images
      .filter((img) => isStill(img.url) && !seen.has(img.url))
      .map((img) => {
        seen.add(img.url);
        return img;
      });

  const me = take(gallery.images);
  if (me.length) groups.push({ label: "Meetings & events", images: me });

  // Category descriptions arrive HTML-encoded ("Bar &amp; Lounge"). Decoding
  // here keeps every consumer on plain text - the viewer writes the group
  // name with textContent and would otherwise print the entity verbatim.
  readHotelGalleryCategories(api).forEach((cat) => {
    const images = take(cat.images);
    if (images.length) {
      groups.push({ label: decodeHTML(cat.label || "") || "Gallery", images });
    }
  });

  return groups;
}

function galleryPanel(gallery) {
  if (!gallery.images.filter((i) => isStill(i.url)).length) {
    return emptyPanel("No meeting and event images published.");
  }
  const images = gallery.images.filter((i) => isStill(i.url));
  const tiles = images
    .slice(0, 24)
    .map(
      (img, i) => `<button type="button" class="aag-me-shot"
        onclick="event.stopPropagation(); aagMeViewerOpen(this, ${i});">
        <img data-src="${esc(sized(img.url, "L"))}"
             alt="${esc(img.alt || "Meeting room")}" loading="lazy" />
      </button>`,
    )
    .join("");
  return `<div class="aag-me-panel-head"><h3>M&amp;E gallery</h3></div>
    <div class="aag-me-shots">${tiles}</div>`;
}

// Full-bleed viewer per the spec: stage on the left, collapsible category list
// on the right, caption bottom-left, counter and arrows bottom-right.
function galleryViewer(groups, hotelName) {
  if (!groups.length) return "";

  let n = 0;
  const flat = [];
  const sections = groups
    .map((group, gi) => {
      const thumbs = group.images
        .map((img) => {
          const index = n++;
          flat.push({ ...img, group: group.label });
          return `<button type="button" class="aag-me-viewer-thumb"
            data-index="${index}"
            onclick="event.stopPropagation(); aagMeViewerGo(this, ${index});">
            <img data-src="${esc(sized(img.url, "M"))}" alt="${esc(
              img.alt || group.label,
            )}" loading="lazy" />
          </button>`;
        })
        .join("");
      // Only the first group starts open; with eight or more categories an
      // all-open sidebar is one long scroll.
      // Deliberately a <div>: reveal treats a nested <section> as a vertical
      // slide, which turned this slide into a stack and added phantom slides.
      return `<div class="aag-me-viewer-group${gi === 0 ? " is-open" : ""}">
        <button type="button" class="aag-me-viewer-group-head"
          onclick="event.stopPropagation(); aagMeViewerGroup(this);">
          <span>${text(group.label)} (${group.images.length})</span>
          <svg xmlns="http://www.w3.org/2000/svg" height="18" width="18"
               viewBox="0 -960 960 960" fill="currentcolor"><path d="M480-345 240-585l56-56 184 184 184-184 56 56-240 240Z"/></svg>
        </button>
        <div class="aag-me-viewer-thumbs">${thumbs}</div>
      </div>`;
    })
    .join("");

  // Alt text is usually already "<hotel> - <subject>", so the hotel name is
  // only prefixed when the caption does not carry it.
  const caption = (img) => {
    const subject = img.alt || img.caption || "";
    if (!hotelName) return subject;
    if (!subject) return hotelName;
    return subject.toLowerCase().includes(hotelName.toLowerCase())
      ? subject
      : `${hotelName} - ${subject}`;
  };

  // Each slide carries its position within its own group: a counter reading
  // "4 / 34" across every category disagreed with the grid's "21 images".
  const groupTotals = {};
  groups.forEach((g) => {
    groupTotals[g.label] = g.images.length;
  });
  const groupSeen = {};

  const slides = flat
    .map((img) => {
      groupSeen[img.group] = (groupSeen[img.group] || 0) + 1;
      return `{"full":"${esc(sized(img.url, "XL"))}","caption":"${esc(
        caption(img),
      )}","group":"${esc(img.group)}","gi":${groupSeen[img.group]},"gt":${
        groupTotals[img.group]
      }}`;
    })
    .join(",");

  return `<div class="aag-me-viewer" data-index="0"
       data-slides='[${slides}]' onclick="event.stopPropagation();">
    <div class="aag-me-viewer-stage">
      <!-- The frame shrink-wraps the photo so the caption and pager below it line
           up with the picture rather than with the letterboxed <img> box. -->
      <div class="aag-me-viewer-frame">
      <img class="aag-me-viewer-img" src="" alt="" />
      <div class="aag-me-viewer-bar">
        <p class="aag-me-viewer-caption"></p>
        <div class="aag-me-viewer-pager">
          <button type="button" onclick="event.stopPropagation(); aagMeViewerStep(this, -1);" aria-label="Previous">
            <svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 -960 960 960" fill="currentcolor"><path d="M560-240 320-480l240-240 56 56-184 184 184 184-56 56Z"/></svg>
          </button>
          <span class="aag-me-viewer-group-name"></span>
          <span class="aag-me-viewer-count"></span>
          <button type="button" onclick="event.stopPropagation(); aagMeViewerStep(this, 1);" aria-label="Next">
            <svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 -960 960 960" fill="currentcolor"><path d="M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z"/></svg>
          </button>
        </div>
      </div>
      </div>
    </div>
    <aside class="aag-me-viewer-side">${sections}</aside>
    <button type="button" class="aag-me-viewer-close"
      onclick="event.stopPropagation(); aagMeViewerClose(this);" aria-label="Close">
      <svg xmlns="http://www.w3.org/2000/svg" height="26" width="26" viewBox="0 -960 960 960" fill="currentcolor"><path d="M256-200.62 200.62-256l224-224-224-224L256-759.38l224 224 224-224L759.38-704l-224 224 224 224L704-200.62l-224-224-224 224Z"/></svg>
    </button>
  </div>`;
}

function diningPanel(outlets) {
  if (!outlets.length) {
    return emptyPanel("No food and beverage outlets published.");
  }
  const cards = outlets
    .map((o) => {
      const hours = o.hours
        .slice(0, 4)
        .map(
          (h) => `<div class="aag-me-hours-row">
            <span>${text(h.type || "Opening")}</span>
            <span>${text(
              [h.from, h.to].filter(Boolean).join(" - ") || "-",
            )}</span>
          </div>`,
        )
        .join("");
      // Card per outlet: photo on top, then name, cuisine, description and
      // hours - so several outlets read as a set rather than a list of rows.
      return `<article class="aag-me-outlet">
        ${
          o.hero
            ? `<div class="aag-me-outlet-media"><img data-src="${esc(
                sized(o.hero, "L"),
              )}" alt="${esc(o.name)}" loading="lazy" /></div>`
            : `<div class="aag-me-outlet-media aag-me-outlet-media--empty"></div>`
        }
        <div class="aag-me-outlet-body">
          <h4>${text(o.name)}</h4>
          ${o.cuisine.length ? tagHtml(o.cuisine) : ""}
          ${o.description ? `<p>${text(o.description)}</p>` : ""}
          ${o.services.length ? tagHtml(o.services.slice(0, 6)) : ""}
          ${hours ? `<div class="aag-me-hours">${hours}</div>` : ""}
        </div>
      </article>`;
    })
    .join("");
  return `<div class="aag-me-panel-head"><h3>Food &amp; beverage</h3></div>
    <div class="aag-me-outlets">${cards}</div>`;
}

/* ── main ─────────────────────────────────────────────────────────────────── */

export function atAGlanceMEParser(block, theme_json) {
  const data = block?.data;
  if (!data) return "";

  const api = data.apiData || {};
  const hotel = api?.hotel || api?.hotelInfo?.collection?.[0] || null;
  const hm = hotel?.metadata || {};
  const hc = hotel?.content || {};
  const apiKey = theme_json?.googleApiKey;

  const meeting = readMeetingRooms(api);
  const features = readMeetingFeatures(api);
  const outlets = readOutlets(api);
  const gallery = readMeetingGallery(api, meeting.rooms);
  const galleryGroupList = galleryGroups(api, gallery);

  const roomContent =
    api?.roomInfo?.collection?.[0]?.content || hc?.roomInfo?.content || {};

  const hotelName = decodeHTML(data.hotelName || "") ||
    decodeHTML(hm?.tmsHotelCode?.description || "");
  const title = decodeHTML(data.title || hc?.text?.content?.title || "");
  const description = decodeHTML(
    data.description || hc?.text?.content?.shortDescription || "",
  );
  const city = decodeHTML(hm?.city?.description || "");
  const country = decodeHTML(hm?.country?.description || "");
  const hotelUrl = hotel?.linkUrl || "";
  // Published either bare or wrapped in {values: []}, like the rest of the
  // repeatable metadata - both shapes are read, as the base layout does.
  const hotelLocation = decodeHTML(
    hm?.location?.values?.[0]?.description || hm?.location?.description || "",
  );

  const overrides = data.imageOverrides || {};
  const picked = api?.images || api?.assets?.picked || {};
  const slotUrl = (slot) =>
    overrides[slot] || picked?.[slot]?.links?.cdnLinks?.[0] || "";
  const heroImage = overrides.hero || hc?.image?.binaryContent?.url || "";
  const heroOpacity = data.heroOpacity != null ? data.heroOpacity : 0.73;

  const brandLogos = theme_json?.brandLogos || {};
  const brandCode = hm?.brand?.key || "";
  const brandLogoSvg = brandCode && brandLogos[brandCode] ? brandLogos[brandCode] : "";

  // Arrival directions come from the hotelOTA document, so nothing here is
  // author-entered. Each row is one leg of one journey ("From the airport" /
  // "By metro"); only the first paragraph is shown, because the rest is
  // usually a travel tip and this is a card over the map, not a page.
  const arrival = readArrival(api);
  const arrivalRows = arrival.groups.flatMap((group) =>
    group.legs
      .filter((leg) => leg.paragraphs.length)
      .map((leg) => [
        [group.title, leg.label].filter(Boolean).join(" - "),
        leg.paragraphs[0],
      ]),
  );

  // The services list is the only place the breakfast offer is published.
  const services = list(hc?.service)
    .map((s) => decodeHTML(((s && s.service) || s)?.description || ""))
    .filter(Boolean);
  const hasBreakfast = services.some((s) => /breakfast/i.test(s));

  const mapLat = hc?.coordinates?.latitude;
  const mapLng = hc?.coordinates?.longitude;
  const hasMap =
    !!apiKey &&
    mapLat != null &&
    mapLng != null &&
    !Number.isNaN(Number(mapLat)) &&
    !Number.isNaN(Number(mapLng));

  const stats = [
    [roomContent?.numOfBedrooms, "Guest<br/>rooms"],
    [roomContent?.numOfMeetingRooms, "Meeting<br/>rooms"],
    [roomContent?.maxCapacity, "Max<br/>cap"],
  ]
    .filter(([v]) => v != null && String(v).trim() !== "")
    .map(
      ([v, label]) => `<div class="hotel-capacity-group aag-me-stat">
        <span class="hotel-capacity-value aag-me-stat-value">${text(v)}</span>
        <span class="hotel-capacity-label aag-me-stat-label">${label}</span>
      </div>`,
    )
    .join("");

  const breakfastStat = hasBreakfast
    ? `<div class="hotel-capacity-group aag-me-stat">
         <span class="hotel-capacity-value aag-me-stat-value aag-me-tick">&#10004;</span>
         <span class="hotel-capacity-label aag-me-stat-label">Breakfast<br/>buffet</span>
       </div>`
    : "";

  const images = ["guestRoom", "meetingRoom"]
    .map((slot) => {
      const url = slotUrl(slot);
      return url
        ? `<div class="aag-me-img"><img data-src="${esc(sized(url, "L"))}"
             data-full="${esc(sized(url, "3XL"))}" alt="${esc(
            slot,
          )}" onclick="event.stopPropagation(); aagOpenFullscreen(this);" /></div>`
        : `<div class="aag-me-img aag-me-img-empty"></div>`;
    })
    .join("");

  const nav = [
    hasMap || arrivalRows.length
      ? navBtn("map", "Location<br/>map", ICONS.map)
      : "",
    navBtn("capacity", "Capacity<br/>chart", ICONS.capacity),
    navBtn("features", "M&amp;E<br/>features", ICONS.features),
    navBtn("gallery", "M&amp;E<br/>gallery", ICONS.gallery),
    navBtn("dining", "Food &amp;<br/>beverage", ICONS.dining),
    navBtn("keyfeatures", "Key<br/>features", ICONS.keyfeatures),
  ]
    .filter(Boolean)
    .join("");

  // The same INDUSTRY SOLUTIONS / KEY FEATURES panel at-a-glance shows, built
  // by the shared module so the two cannot drift. No brand logo here - the
  // cover already carries it - and the builder drops the container entirely
  // when none is passed, so the original layout keeps its own.
  const expandSection = buildAagExpandSection(hc, "");

  // Directions sit over the map rather than in the right column: they describe
  // the location, so they belong with it.
  const arrivalOverlay = arrivalRows.length
    ? `<button type="button" class="aag-me-map-toggle"
         onclick="event.stopPropagation(); aagMeMapInfo(this);">
         <span class="aag-me-map-toggle-icon">${ICONS.map}</span>
         <span class="aag-me-map-toggle-label">Getting here</span>
       </button>
       <div class="aag-me-map-overlay">
         <dl class="aag-me-distances">${arrivalRows
           .map(
             ([label, value]) =>
               `<div class="aag-me-distance"><dt>${text(
                 label,
               )}</dt><dd>${text(value)}</dd></div>`,
           )
           .join("")}</dl>
       </div>`
    : "";

  const panels = [
    panel(
      "map",
      `<div class="aag-me-map-wrap">${
        hasMap
          ? `<iframe data-src="/map.html?entity=true&lat=${esc(mapLat)}&lng=${esc(
              mapLng,
            )}&zoom=16&key=${esc(apiKey)}" frameborder="0" title="Location map"></iframe>`
          : emptyPanel("No coordinates published for this hotel.")
      }${arrivalOverlay}</div>`,
    ),
    panel("capacity", capacityPanel(meeting, theme_json)),
    panel("features", featuresPanel(features, meeting)),
    panel("gallery", galleryPanel(gallery)),
    panel("dining", diningPanel(outlets)),
    panel("keyfeatures", expandSection),
  ].join("");

  const closeBtnSvg =
    '<svg xmlns="http://www.w3.org/2000/svg" height="28" viewBox="0 -960 960 960" width="28" fill="currentcolor"><path d="M256-200.62 200.62-256l224-224-224-224L256-759.38l224 224 224-224L759.38-704l-224 224 224 224L704-200.62l-224-224-224 224Z"/></svg>';

  return `<div class="at-a-glance-content-container aag-me initial" data-panel="hero">
    <div class="at-a-glance-left">
      ${
        heroImage
          ? `<img class="at-a-glance-hero" data-src="${esc(
              sized(heroImage, "XL"),
            )}" data-full="${esc(sized(heroImage, "3XL"))}" alt="${esc(
              hotelName,
            )}" style="opacity: ${esc(
              heroOpacity,
            )};" onclick="aagOpenFullscreen(this);" />`
          : ""
      }
      <div class="content_block initial-overlay">
        <h2 class="initial-overlay-title">${text(hotelName)}</h2>
        <button class="view-details-btn" onclick="initialTransition(this)">
          <span class="view-details-btn-text">View Details &#8594;</span>
        </button>
      </div>
      <div class="aag-me-hero-overlay" data-panel-body="hero">
        ${brandLogoSvg ? `<div class="hotel-logo-container">${brandLogoSvg}</div>` : ""}
        ${title ? `<h3 class="aag-me-hero-title">${text(title)}</h3>` : ""}
        ${description ? `<p class="aag-me-hero-body">${text(description)}</p>` : ""}
      </div>
      ${panels}
    </div>

    <div class="at-a-glance-right">
      <div class="aag-me-images">${images}</div>
      ${
        stats || breakfastStat
          ? `<div class="hotel-capacity-container aag-me-stats">${stats}${breakfastStat}</div>`
          : ""
      }
      <div class="hotel-heading-container aag-me-place">
        <div class="location-pin-icon aag-me-pin">${HotelLocation}</div>
        ${country ? `<p class="hotel-country aag-me-country">${text(country)}</p>` : ""}
        ${city ? `<h3 class="hotel-city aag-me-city">${text(city)}</h3>` : ""}
        ${hotelName ? `<p class="hotel-name aag-me-hotel">${text(hotelName)}</p>` : ""}
        ${
          hotelUrl
            ? `<a class="aag-me-website" href="https://www.radissonhotels.com${esc(
                hotelUrl,
              )}" target="_blank" rel="noopener noreferrer"
                onclick="event.stopPropagation();">VISIT HOTEL WEBSITE</a>`
            : ""
        }
        ${
          hotelLocation
            ? `<p class="hotel-footer-location-container aag-me-location">
                 <span class="hotel-footer-location-label aag-me-location-label">Location:</span>
                 <span class="hotel-footer-location aag-me-location-value">${text(hotelLocation)}</span>
               </p>`
            : ""
        }
      </div>
      <nav class="aag-me-nav">${nav}</nav>
    </div>

    <div class="aag-fullscreen-viewer" onclick="aagCloseFullscreen(this.querySelector('.aag-fullscreen-close'));">
      <button class="aag-fullscreen-close" onclick="event.stopPropagation(); aagCloseFullscreen(this);">${closeBtnSvg}</button>
      <img class="aag-fullscreen-img" src="" alt="Full size image" onclick="event.stopPropagation();">
    </div>
    <button class="initial-back-btn" aria-label="Back to cover"
      onclick="event.stopPropagation(); aagMeInitialBack(this)">
      <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960"
           width="24px" fill="currentcolor">
        <path d="m560-240-56-58 142-142H160v-80h486L504-662l56-58 240 240-240 240Z"/>
      </svg>
    </button>
    ${galleryViewer(galleryGroupList, hotelName)}
    <button class="aag-expand-btn aag-me-expand-cols" aria-expanded="false"
      aria-label="Expand to full width"
      onclick="event.stopPropagation(); aagMeExpandColumns(this)">
      <svg xmlns="http://www.w3.org/2000/svg" width="8" height="16"
           viewBox="0 0 8 16" fill="none" stroke="currentcolor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m1.5 1.5 5 6.5-5 6.5"/></svg>
    </button>
  </div>`;
}
